import { Form, Input, Modal, Select } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { AuthenticationState } from "../../interfaces/users";
import { api } from "../../config";

interface RoutingZoneModalProps {
  isOpen: boolean;
  onClose: () => void;
  routingZone?: any;
  refetch: () => void;
  users: any[];
  municipalities: any[];
  neighborhoods: any[];
}

const CURITIBA_IBGE_ID = 4106902;

const typeOptions = [
  {
    label: "Montagem",
    value: "ASSEMBLE",
  },
  {
    label: "Frete",
    value: "FREIGHT",
  },
];

export const RoutingZoneModal = ({
  isOpen,
  onClose,
  routingZone,
  refetch,
  users,
  municipalities,
  neighborhoods,
}: RoutingZoneModalProps) => {
  const { token } = useSelector((state: AuthenticationState) => state);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [selectedMunicipalityIds, setSelectedMunicipalityIds] = useState<number[]>(
    []
  );

  const municipalityOptions = useMemo(
    () =>
      municipalities.map((municipality) => ({
        label: `${municipality.name} - ${municipality.stateCode}`,
        value: municipality.id,
      })),
    [municipalities]
  );

  const userOptions = useMemo(
    () =>
      users.map((user) => ({
        label: user.fullName,
        value: user.id,
      })),
    [users]
  );

  const curitibaMunicipality = useMemo(
    () =>
      municipalities.find(
        (municipality) => Number(municipality.ibgeId) === CURITIBA_IBGE_ID
      ),
    [municipalities]
  );

  const shouldShowNeighborhoods =
    !!curitibaMunicipality &&
    selectedMunicipalityIds.includes(curitibaMunicipality.id);

  const neighborhoodOptions = useMemo(
    () =>
      neighborhoods
        .filter(
          (neighborhood) =>
            neighborhood.active !== false &&
            neighborhood.municipalityId === curitibaMunicipality?.id
        )
        .map((neighborhood) => ({
          label: neighborhood.name,
          value: neighborhood.id,
        })),
    [neighborhoods, curitibaMunicipality]
  );

  useEffect(() => {
    const municipalityIds = routingZone?.municipalityIds ?? [];
    form.setFieldsValue({
      name: routingZone?.name ?? "",
      type: routingZone?.type ?? "ASSEMBLE",
      userIds: routingZone?.userIds ?? [],
      municipalityIds,
      neighborhoodIds: routingZone?.neighborhoodIds ?? [],
    });
    setSelectedMunicipalityIds(municipalityIds);
  }, [form, routingZone, isOpen]);

  useEffect(() => {
    if (!shouldShowNeighborhoods) {
      form.setFieldValue("neighborhoodIds", []);
    }
  }, [form, shouldShowNeighborhoods]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      if (routingZone?.id) {
        await api(token).put(`/routing-zones/${routingZone.id}`, values);
        toast.success("Zona atualizada com sucesso!");
      } else {
        await api(token).post("/routing-zones", values);
        toast.success("Zona criada com sucesso!");
      }

      refetch();
      onClose();
      form.resetFields();
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }

      toast.error(
        error?.response?.data?.error ?? "Ocorreu um erro ao salvar a zona"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      centered
      open={isOpen}
      onCancel={onClose}
      onOk={handleSubmit}
      confirmLoading={loading}
      width="55%"
      title={routingZone?.id ? "Editar Zona" : "Nova Zona"}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="name"
          label="Nome"
          rules={[{ required: true, message: "Informe o nome da zona" }]}
        >
          <Input placeholder="Ex.: Litoral" />
        </Form.Item>
        <Form.Item
          name="type"
          label="Tipo"
          rules={[{ required: true, message: "Selecione o tipo da zona" }]}
        >
          <Select options={typeOptions} />
        </Form.Item>
        <Form.Item name="userIds" label="Usuários">
          <Select
            mode="multiple"
            allowClear
            showSearch
            optionFilterProp="label"
            options={userOptions}
            placeholder="Selecione os usuários"
          />
        </Form.Item>
        <Form.Item name="municipalityIds" label="Municípios">
          <Select
            mode="multiple"
            allowClear
            showSearch
            optionFilterProp="label"
            options={municipalityOptions}
            placeholder="Selecione os municípios"
            onChange={(values) => setSelectedMunicipalityIds(values)}
          />
        </Form.Item>
        {shouldShowNeighborhoods ? (
          <Form.Item
            name="neighborhoodIds"
            label="Bairros de Curitiba"
            rules={[
              {
                required: true,
                message:
                  "Ao selecionar Curitiba, é obrigatório informar ao menos um bairro",
              },
            ]}
          >
            <Select
              mode="multiple"
              allowClear
              showSearch
              optionFilterProp="label"
              options={neighborhoodOptions}
              placeholder="Selecione os bairros de Curitiba"
            />
          </Form.Item>
        ) : null}
      </Form>
    </Modal>
  );
};
